import type { OrderStatus } from '@flux/shared';
import { CircleAlert, RefreshCw, Send, XCircle } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.js';
import { Button } from '@/components/ui/button.js';
import { IgnoreReasonDialog } from './ignore-reason-dialog.js';
import { isBulkActionAvailableForAny } from './order-status-actions.js';
import { useBulkOrderAction } from './use-order-actions.js';

export interface BulkActionBarProps {
  selectedIds: readonly string[];
  /** Statuts des seules lignes Shopify sélectionnées — une facture n'est pas sélectionnable. */
  selectedStatuses: readonly OrderStatus[];
  /** Vide la sélection — appelé seulement quand l'action a réussi pour toute la sélection. */
  onDone: () => void;
}

/**
 * Barre d'actions groupées (`02` §3 : « sélection multiple pour Envoyer
 * maintenant, Renvoyer à Martelet, Marquer ignorée »). Chaque commande de la sélection
 * réussit ou échoue indépendamment (`OrderBulkActionResult`) : les échecs
 * individuels restent affichés sous la barre avec leur message exact
 * (`02` §0), même après le toast de résumé.
 */
export function BulkActionBar({
  selectedIds,
  selectedStatuses,
  onDone,
}: BulkActionBarProps): JSX.Element | null {
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [ignoreOpen, setIgnoreOpen] = useState(false);
  const bulkAction = useBulkOrderAction();

  if (selectedIds.length === 0) {
    return null;
  }

  const canSendNow = isBulkActionAvailableForAny('send-now', selectedStatuses);
  const canRegenerate = isBulkActionAvailableForAny('regenerate', selectedStatuses);
  const canIgnore = isBulkActionAvailableForAny('ignore', selectedStatuses);

  /** Ne vide la sélection que si l'action a réussi pour toutes les commandes — sinon les échecs restent affichés. */
  function clearSelectionIfFullSuccess(result: { failed: readonly unknown[] }): void {
    if (result.failed.length === 0) {
      onDone();
    }
  }

  return (
    <div className="flex flex-col rounded-lg border border-border bg-selection">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2">
        <span className="text-sm font-medium tabular-nums">
          {selectedIds.length} commande{selectedIds.length > 1 ? 's' : ''} sélectionnée
          {selectedIds.length > 1 ? 's' : ''}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canSendNow || bulkAction.isPending}
            onClick={() => {
              bulkAction.mutate(
                { kind: 'send-now', orderIds: selectedIds },
                { onSuccess: clearSelectionIfFullSuccess },
              );
            }}
          >
            <Send aria-hidden="true" />
            Envoyer maintenant
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canRegenerate || bulkAction.isPending}
            onClick={() => {
              setConfirmRegenerate(true);
            }}
          >
            <RefreshCw aria-hidden="true" />
            Renvoyer à Martelet
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canIgnore || bulkAction.isPending}
            onClick={() => {
              setIgnoreOpen(true);
            }}
          >
            <XCircle aria-hidden="true" />
            Marquer ignorée
          </Button>
        </div>
      </div>

      {bulkAction.data && bulkAction.data.failed.length > 0 ? (
        <ul className="flex flex-col gap-1.5 border-t border-border px-3 py-2.5 text-sm">
          {bulkAction.data.failed.map((failure) => (
            <li key={failure.orderId} className="flex items-start gap-2 text-status-red-fg">
              <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span className="min-w-0 break-words">{failure.message}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <ConfirmDialog
        open={confirmRegenerate}
        onOpenChange={setConfirmRegenerate}
        title="Renvoyer les commandes sélectionnées à Martelet"
        description={`Les ${selectedIds.length} commande(s) sélectionnée(s) seront relues dans Shopify, puis un nouveau fichier sera généré et envoyé pour chacune. Une commande déjà intégrée par Martelet peut y être en double. Les anciens fichiers restent consultables.`}
        confirmLabel="Renvoyer"
        isConfirming={bulkAction.isPending}
        onConfirm={() => {
          bulkAction.mutate(
            { kind: 'regenerate', orderIds: selectedIds },
            {
              onSuccess: (result) => {
                setConfirmRegenerate(false);
                clearSelectionIfFullSuccess(result);
              },
            },
          );
        }}
      />

      <IgnoreReasonDialog
        open={ignoreOpen}
        onOpenChange={setIgnoreOpen}
        description={`${selectedIds.length} commande(s) seront marquées ignorées.`}
        isConfirming={bulkAction.isPending}
        onConfirm={(reason) => {
          bulkAction.mutate(
            { kind: 'ignore', orderIds: selectedIds, reason },
            {
              onSuccess: (result) => {
                setIgnoreOpen(false);
                clearSelectionIfFullSuccess(result);
              },
            },
          );
        }}
      />
    </div>
  );
}
